import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ComponentType from 'common/src/app/data/enum/ComponentType';
import ContactUsFieldNames from 'common/src/app/data/enum/FieldNames/ContactUsFieldNames';
import InputType from 'common/src/app/data/enum/InputType';
import { withFunctionalClassName } from 'common/src/app/util/componentClassNameUtils';
import Textarea from '../../atoms/Textarea';
import Button from '../../atoms/Button';
import Select from '../../atoms/Select';
import Loader from '../../atoms/Loader';
import FormikError from '../../atoms/FormikError';
import TextNew, { Colors, ElementTypes, FontWeight } from '../../atoms/TextNew';
import ContactUsSuccessMessage from '../../molecules/ContactUsSuccessMessage';

import ContactUsMemberForm from './components/molecules/ContactUsMemberForm';

import './contact-us-form.scss';

const ContactUsForm = (
  {
    errors,
    successMessageSubjectId,
    values,
    userType,
    handleSubmit,
    handleChange,
    handleBlur,
    setSuccessMessage,
    setErrors,
    fetchSubjectOptions,
    children,
    loading,
    hasErrorBoundary,
  },
  { getMessage },
  className,
  dataTestId,
) => {
  const [subjects, setSubjects] = useState([]);
  const [isLoadingSubjects, setIsLoadingSubjects] = useState(true);

  const { subjectId } = values;
  const currentSubject = subjects.find(s => s.value === values.subjectId);
  const showThirdPartyDisclaimer = currentSubject?.requiresThirdPartyDataSharing;

  useEffect(() => () => setSuccessMessage?.(null), []);

  useEffect(() => {
    const loadSelectOptions = async () => {
      try {
        const retrievedSubjects = await fetchSubjectOptions();

        if (!Array.isArray(retrievedSubjects) || retrievedSubjects.length === 0) {
          setIsLoadingSubjects(false);
          setErrors({ generalError: 'contactUsForm.error.subjects' });
          return;
        }

        let sortedSubjects = retrievedSubjects.sort((a, b) => a.displayOrder - b.displayOrder);
        sortedSubjects = [
          { title: getMessage('contactUsForm.placeholder.subject'), value: '' },
          ...sortedSubjects,
        ];

        setIsLoadingSubjects(false);
        setSubjects(sortedSubjects);
      } catch (error) {
        setIsLoadingSubjects(false);
        setErrors({ generalError: 'contactUsForm.error.subjects' });
      }
    };

    loadSelectOptions();
  }, []);

  return (
    <div className={className}>
      {successMessageSubjectId ? (
        <header>
          <ContactUsSuccessMessage />
        </header>
      ) : (
        <>
          <TextNew.Sans.SM
            weight={FontWeight.LIGHT}
            localeId="contactUsForm.body"
            localeParams={{
              PREFER: (
                <TextNew.Sans.SM
                  key="contact-us-prefer-email"
                  weight={FontWeight.NORMAL}
                  localeId="contactUsForm.prefer"
                  element={ElementTypes.SPAN}
                />
              ),
            }}
          />
          {isLoadingSubjects && <Loader />}
          {subjects.length > 1 && (
            <form onSubmit={handleSubmit}>
              <Select
                customLabel={getMessage('contactUsForm.placeholder.subject')}
                defaultValue={subjectId}
                dataTestid={`${dataTestId}-select`}
                onChange={handleChange}
                name={ContactUsFieldNames.SUBJECT_ID}
                options={subjects}
                disabled={hasErrorBoundary}
              />

              {subjectId !== '' && (
                <>
                  {children}
                  <Textarea
                    data-testid={`${dataTestId}-message`}
                    rows="4"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    name={ContactUsFieldNames.MESSAGE}
                    type={InputType.TEXT}
                    placeholder={getMessage('contactUsForm.contactMessage')}
                  />
                  <FormikError
                    dataTestid={`${dataTestId}-error`}
                    name={ContactUsFieldNames.MESSAGE}
                  />
                  {showThirdPartyDisclaimer && (
                    <TextNew.Sans.SM
                      element={ElementTypes.SMALL}
                      weight={FontWeight.LIGHT}
                      localeId="contactUsForm.thirdPartyDisclaimer"
                    />
                  )}
                  <Button
                    type={InputType.SUBMIT}
                    disabled={loading}
                    dataTestid={`${dataTestId}-button`}
                  >
                    <TextNew.Sans.SM
                      element={ElementTypes.SPAN}
                      color={Colors.PRIMARY_LIGHT}
                      localeId="contactUsForm.cta"
                    />
                  </Button>
                </>
              )}
            </form>
          )}
        </>
      )}

      {errors?.generalError && (
        <TextNew.Error localeId={errors.generalError} cid="error" color={Colors.BRAND} />
      )}
    </div>
  );
};

ContactUsForm.propTypes = {
  children: PropTypes.node,
  handleSubmit: PropTypes.func,
  handleBlur: PropTypes.func,
  handleChange: PropTypes.func,
  values: PropTypes.object,
  errors: PropTypes.object,
  userType: PropTypes.string,
  successMessageSubjectId: PropTypes.string,
  setSuccessMessage: PropTypes.func,
  setErrors: PropTypes.func,
  fetchSubjectOptions: PropTypes.func,
  loading: PropTypes.bool,
  hasErrorBoundary: PropTypes.bool,
};

ContactUsForm.contextTypes = {
  getMessage: PropTypes.func.isRequired,
};

export { ContactUsMemberForm };

export default withFunctionalClassName(ComponentType.ORGANISM, 'ContactUsForm')(ContactUsForm);
